import React, { useMemo, useState } from 'react';

import { RowButton } from '@eika/button';
import { SearchInput } from '@eika/input';
import { Modal } from '@eika/modal';

import { capitalize, isMostCommonCurrency } from '../functions/type-functions';
import { CalculatorFieldName, CurrencyDetails, Piwik, SiteCoreDictionary } from '../types';

import './SelectCurrencyModal.scss';

interface SelectCurrencyProps {
    onChangeCurrency: (currency: CurrencyDetails, field: CalculatorFieldName) => void;
    showSelectCurrencyModalWithField: CalculatorFieldName | undefined;
    availableCurrencies: CurrencyDetails[];
    /** A list of length 2 that contains the FROM and TO selected currency codes */
    selectedCurrencyCodes: string[];
    dictionary: SiteCoreDictionary;
    piwik: Piwik | undefined;
}

interface SelectCurrencyModalProps extends SelectCurrencyProps {
    setShowSelectCurrencyModalWithField: (field: CalculatorFieldName | undefined) => void;
    onChangeCurrency: (currency: CurrencyDetails, field: CalculatorFieldName) => void;
}

type CurrencyOptionsDisplayProps = {
    header: string;
    currencyList: CurrencyDetails[];
    piwik: Piwik | undefined;
    selectedCurrencyCodes: string[];
    onChangeCurrency: (currency: CurrencyDetails, field: CalculatorFieldName) => void;
    showSelectCurrencyModalWithField: CalculatorFieldName | undefined;
};

const CurrencyOptionsDisplay = ({
    header,
    currencyList,
    piwik,
    selectedCurrencyCodes,
    onChangeCurrency,
    showSelectCurrencyModalWithField,
}: CurrencyOptionsDisplayProps) => {
    if (currencyList.length === 0) {
        return null;
    }

    return (
        <div className="currency-options-container">
            <h2 className="currency-options-category">{header}</h2>
            <div className="currency-options">
                {currencyList.map((currency) => (
                    <RowButton
                        className="select-currency-button"
                        data-track-id={piwik?.visaCalculator?.visaExchangeCalculatorSelectCurrencyOption?.dataTrackId}
                        data-track-linkname={
                            piwik?.visaCalculator?.visaExchangeCalculatorSelectCurrencyOption?.dataTrackLinkname
                        }
                        disabled={selectedCurrencyCodes.includes(currency.currencyCode)}
                        id={piwik?.visaCalculator?.visaExchangeCalculatorSelectCurrencyOption?.dataTrackId}
                        key={currency.currencyCode}
                        name="visa-exchange-calculator-select-currency-option"
                        onClick={() => {
                            if (showSelectCurrencyModalWithField) {
                                onChangeCurrency(currency, showSelectCurrencyModalWithField);
                            }
                        }}
                    >
                        <div className="select-currency-name">
                            <span className="currency-code">{currency.currencyCode}</span>
                            <span>{`\u2014`}</span>
                            <span>{capitalize(currency.displayName)}</span>
                        </div>
                    </RowButton>
                ))}
            </div>
        </div>
    );
};

const SelectCurrency = ({
    onChangeCurrency,
    showSelectCurrencyModalWithField,
    availableCurrencies,
    selectedCurrencyCodes,
    dictionary,
    piwik,
}: SelectCurrencyProps) => {
    const { visaCalculatorCurrencySearch, visaCalculatorCurrencyOptionsMostCommon, visaCalculatorCurrencyOptionsAll } =
        dictionary;
    const [searchText, setSearchText] = useState<string>('');

    const availableCurrenciesAffectedBySearch = useMemo(() => {
        const includesSearchText = ({ currencyCode, displayName }: CurrencyDetails) => {
            const lowercaseSearchText = searchText.toLowerCase();

            if (lowercaseSearchText !== '') {
                if (
                    !currencyCode.toLowerCase().includes(lowercaseSearchText) &&
                    !displayName.toLowerCase().includes(lowercaseSearchText)
                ) {
                    return false;
                }
            }

            return true;
        };

        return availableCurrencies.filter(includesSearchText);
    }, [availableCurrencies, searchText]);

    if (typeof showSelectCurrencyModalWithField === 'undefined') {
        return null;
    }

    return (
        <section>
            <div className="search-input-container">
                <SearchInput
                    data-track-id={piwik?.visaCalculator?.visaExchangeCalculatorSelectCurrencySearch?.dataTrackId}
                    data-track-linkname={
                        piwik?.visaCalculator?.visaExchangeCalculatorSelectCurrencySearch?.dataTrackLinkname
                    }
                    id={piwik?.visaCalculator?.visaExchangeCalculatorSelectCurrencySearch?.dataTrackId}
                    onChange={({ target }) => setSearchText(target.value)}
                    placeholder={visaCalculatorCurrencySearch}
                    value={searchText}
                />
            </div>

            <CurrencyOptionsDisplay
                header={visaCalculatorCurrencyOptionsMostCommon}
                currencyList={availableCurrenciesAffectedBySearch.filter(isMostCommonCurrency)}
                piwik={piwik}
                selectedCurrencyCodes={selectedCurrencyCodes}
                onChangeCurrency={onChangeCurrency}
                showSelectCurrencyModalWithField={showSelectCurrencyModalWithField}
            />
            <CurrencyOptionsDisplay
                header={visaCalculatorCurrencyOptionsAll}
                currencyList={availableCurrenciesAffectedBySearch}
                piwik={piwik}
                selectedCurrencyCodes={selectedCurrencyCodes}
                onChangeCurrency={onChangeCurrency}
                showSelectCurrencyModalWithField={showSelectCurrencyModalWithField}
            />
        </section>
    );
};

const SelectCurrencyModal = (props: SelectCurrencyModalProps) => {
    const { showSelectCurrencyModalWithField, setShowSelectCurrencyModalWithField, piwik } = props;
    const { visaCalculatorCurrency } = props.dictionary;
    const show = typeof showSelectCurrencyModalWithField !== 'undefined';

    return (
        <Modal
            className="select-currency-modal"
            data-track-id={piwik?.visaCalculator?.visaExchangeCalculatorSelectCurrency?.dataTrackId}
            data-track-linkname={piwik?.visaCalculator?.visaExchangeCalculatorSelectCurrency?.dataTrackLinkname}
            id={piwik?.visaCalculator?.visaExchangeCalculatorSelectCurrency?.dataTrackId}
            setOpen={() => setShowSelectCurrencyModalWithField(undefined)}
            shouldCloseOnOverlayClick
            open={show}
            title={visaCalculatorCurrency}
        >
            <SelectCurrency {...props} />
        </Modal>
    );
};

export default SelectCurrencyModal;
